import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./style.module.scss";

interface CountdownTimerProps {
  validUntil: string; // ISO 8601 datetime string
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ validUntil }) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [isExpired, setIsExpired] = useState<boolean>(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const targetTime = new Date(validUntil).getTime();
      const difference = targetTime - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft(0);
        return;
      }

      setTimeLeft(difference);
      setIsExpired(false);
    };

    // Calculate immediately
    calculateTimeLeft();

    // Update every second
    const interval = setInterval(calculateTimeLeft, 1000);

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, [validUntil]);

  const formatTime = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  const getColorClass = (): string => {
    const totalSeconds = Math.floor(timeLeft / 1000);

    if (totalSeconds > 180) return styles.green; // > 3 minutes
    if (totalSeconds > 60) return styles.orange; // > 1 minute
    return styles.red; // <= 1 minute
  };

  if (isExpired) {
    return (
      <span className={`${styles.timer} ${styles.expired}`}>
        {t("expired")}
      </span>
    );
  }

  return (
    <span className={`${styles.timer} ${getColorClass()}`}>
      {formatTime(timeLeft)}
    </span>
  );
};

export default CountdownTimer;
